/**
 * rewards-state.js — Enrichment custom script (not a Sleek theme file).
 * 2026-09-17: renamed from loyalty-state.js, own request -- "reference
 * EnrichmentRewards instead of EnrichmentLoyalty" (this file's global was
 * EnrichmentLoyaltyState, now EnrichmentRewardsState) throughout.
 *
 * Single shared points-balance state for the whole page: rewards-banner.js
 * (mega menu, several instances), loyalty-rewards.js (/pages/rewards), and
 * cart.js's CartLoyaltyRewards (cart-drawer redemption modal) all read and
 * write through THIS module instead of each keeping its own independent
 * sessionStorage cache with no awareness of the others.
 *
 * 2026-09-17, own request: "keep an internal state variable with the
 * points balance and refresh every so often... refresh it on events like
 * addition of points or redemption or cancellation of redemption etc."
 * Two distinct refresh paths, both landing in the same place:
 *
 *   1. PASSIVE POLLING -- startAutoRefresh() runs one shared interval
 *      (not one per consumer) that calls refresh() on a cadence; refresh()
 *      itself is a no-op if the cache is still within FRESH_MS, so this
 *      never fetches more often than that regardless of how many
 *      consumers called startAutoRefresh().
 *   2. EVENT-DRIVEN -- any consumer that already knows the fresh number
 *      (redeem()/cancel-redemption responses already return the new
 *      balance) calls set() directly with it -- no network round trip
 *      needed, and every OTHER consumer on the page is notified
 *      synchronously via the rewardsBalanceChange pub/sub event. A
 *      redemption on /pages/rewards updates the mega-menu pill
 *      immediately, without waiting for its own poll interval or a
 *      menu-open refresh.
 *
 * Deliberately a plain object on `window`, not a class/custom element --
 * this needs to exist and be callable the instant any of the three
 * consumer scripts runs, in whatever order the theme happens to load them,
 * with no DOM element of its own to hang a customElements lifecycle off.
 *
 * sessionStorage-backed (same enrichment_rewards_balance_cache key
 * rewards-banner.js used to own directly) so a fresh pageview within the
 * same tab session still starts from a recent answer instead of a
 * skeleton, exactly like before -- this module just centralizes who reads
 * and writes it.
 */
(function () {
  if (window.EnrichmentRewardsState) return; // already initialized -- more than one script tag load, safe no-op

  var CACHE_KEY = 'enrichment_rewards_balance_cache';
  var FRESH_MS = 10 * 1000;
  var POLL_MS = 30 * 1000;

  var memoryState = null; // { pointsBalance, hasBirthday, maxRedeemablePoints, cachedAt }
  var inFlightFetch = null;
  var pollTimer = null;
  var knownMeUrl = null;

  function readSession() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      // sessionStorage can throw in locked-down/private-browsing contexts
      // -- treat exactly like a cache miss, never let this break the page.
      return null;
    }
  }

  function writeSession(state) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(state));
    } catch (e) {
      // Same reasoning as the read above -- a failed cache write just
      // means the next check fetches again, not a real problem.
    }
  }

  function publish(state) {
    if (window.FoxTheme && FoxTheme.pubsub) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.rewardsBalanceChange, { data: state });
    }
  }

  /**
   * Writes a fresh balance into shared state and notifies every
   * subscriber, unless `options.silent` is set (used when a consumer is
   * merely seeding state from its own already-rendered server-side data,
   * not reporting an actual change worth telling everyone else about).
   *
   * Merges onto the previous state rather than replacing it wholesale --
   * callers that only know one field changed (redeem()/cancel-redemption
   * responses carry a fresh `pointsBalance` but say nothing about
   * `hasBirthday`) can call set({ pointsBalance }) without that omission
   * being misread as "birthday was unset".
   */
  function set(data, options) {
    options = options || {};
    var previous = get() || {};
    memoryState = {
      pointsBalance: data.pointsBalance != null ? Number(data.pointsBalance) || 0 : previous.pointsBalance || 0,
      hasBirthday: data.hasBirthday != null ? data.hasBirthday === true : previous.hasBirthday === true,
      // 2026-09-17: own request -- "a clickable green pill that opens
      // rewards to prompt you when you have a reward you can redeem" (cart
      // drawer). Carried here so that surface can show/hide itself off the
      // same shared poll instead of firing its own /me fetch just to know
      // this one number -- most callers of set() (redeem/cancel responses)
      // don't know this value, so it falls back to the previous one same
      // as hasBirthday above rather than getting wiped to 0 on every call.
      maxRedeemablePoints:
        data.maxRedeemablePoints != null ? Number(data.maxRedeemablePoints) || 0 : previous.maxRedeemablePoints || 0,
      // 2026-09-23: added for the mega-menu banner's progress-to-next-
      // reward bar -- GET /me already returns both of these (see
      // workers/loyalty/src/index.js's handleGetMe), they just weren't
      // being retained in shared state before. Same fall-back-to-previous
      // pattern as the fields above: redeem()/cancel-redemption responses
      // don't echo these back (the redemption ratio doesn't change on
      // those actions), so a caller that only knows a fresh pointsBalance
      // shouldn't wipe out the last-known threshold/value.
      redemptionThresholdPoints:
        data.redemptionThresholdPoints != null
          ? Number(data.redemptionThresholdPoints) || 0
          : previous.redemptionThresholdPoints || 0,
      redemptionValueFormatted:
        data.redemptionValueFormatted != null ? data.redemptionValueFormatted : previous.redemptionValueFormatted || '',
      cachedAt: Date.now(),
    };
    writeSession(memoryState);
    if (!options.silent) publish(memoryState);
    return memoryState;
  }

  function get() {
    if (memoryState) return memoryState;
    memoryState = readSession();
    return memoryState;
  }

  function isFresh(maxAgeMs) {
    var state = get();
    return !!state && Date.now() - state.cachedAt < (maxAgeMs == null ? FRESH_MS : maxAgeMs);
  }

  var inFlightMeFetch = null;

  /**
   * Shared, deduped raw GET /apps/rewards/me request. Exported (unlike
   * everything else so far, which only ever exposed the narrowed-down
   * balance subset) because loyalty-rewards.js's own page needs the FULL
   * /me payload (redemption options, referral code, pending reward,
   * expiry date, transactions threshold -- none of which memoryState
   * retains, see set()'s own doc comment), so it can't just consume
   * refresh()'s narrowed result the way rewards-banner.js does.
   *
   * 2026-09-18: added -- direct report that /pages/rewards fires TWO real
   * /me requests on every single pageview (this page's own full-payload
   * fetch, previously a totally separate raw `fetch()` call in
   * loyalty-rewards.js, plus the sitewide mega-menu banner's own
   * refresh() call below -- entirely unaware of each other, run close
   * enough together on page load that the natural in-flight dedupe below
   * usually catches them). Own follow-up question -- "should we be using
   * our background state to not make multiple calls" -- yes, this is
   * that fix. ALWAYS a real network call otherwise (no freshness
   * short-circuit -- that stays refresh()'s job, since a caller who wants
   * the freshness check has refresh(), and a caller who calls this
   * directly wants the true current state, not up-to-10-seconds-stale
   * cached balance missing fields it doesn't cache). Never throws for a
   * non-2xx response -- returns `{ ok, json }` so callers inspect status
   * themselves, only a genuine network failure rejects.
   *
   * 2026-10-20: `options.force` added -- own report that saving a
   * birthday left the mega-menu pill stale until a manual refresh. The
   * in-flight dedupe above is right for the case it was built for (two
   * consumers both asking "what's the balance?" on page load), but it is
   * WRONG after a write: a /me request that STARTED BEFORE the POST
   * cannot possibly reflect it, so a consumer re-syncing after a
   * mutation could be handed a pre-mutation payload. That stale payload
   * then went through set(), which stamps cachedAt = now, so the wrong
   * answer also looked fresh for FRESH_MS and suppressed the next
   * refresh() that would have corrected it -- the bug actively masked
   * itself. Callers re-syncing after a write pass { force: true } to get
   * their own request rather than joining one that predates their change.
   */
  function fetchMe(meUrl, options) {
    options = options || {};
    if (inFlightMeFetch && !options.force) return inFlightMeFetch;
    // Each request clears only ITS OWN slot. A forced fetch replaces
    // inFlightMeFetch while the request it overtook is still running, so
    // an unguarded `inFlightMeFetch = null` in the older request's
    // handler would wipe the newer one's entry -- costing the next
    // caller its chance to join and firing a third request instead.
    // Harmless to correctness, wasteful in requests; the identity check
    // below is the whole fix.
    var thisFetch = fetch(meUrl, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, json: json };
        });
      })
      .then(
        function (result) {
          if (inFlightMeFetch === thisFetch) inFlightMeFetch = null;
          return result;
        },
        function (error) {
          if (inFlightMeFetch === thisFetch) inFlightMeFetch = null;
          throw error;
        }
      );
    inFlightMeFetch = thisFetch;
    return thisFetch;
  }

  /**
   * Fetches GET /apps/rewards/me and updates shared state, unless the
   * cache is already fresh (pass {force: true} to bypass that -- used
   * right after an action a consumer already knows changed the balance,
   * e.g. as a defensive re-sync alongside a direct set() call).
   * Concurrent calls from multiple consumers collapse into the one
   * in-flight request rather than each firing its own -- and, via
   * fetchMe() above, also collapse together with a concurrent direct
   * fetchMe() caller (loyalty-rewards.js) sharing the same underlying
   * network request.
   */
  function refresh(meUrl, options) {
    options = options || {};
    meUrl = meUrl || knownMeUrl;
    if (!meUrl) return Promise.resolve(get());
    knownMeUrl = meUrl;

    if (!options.force && isFresh(options.freshMs)) return Promise.resolve(get());
    if (inFlightFetch) return inFlightFetch;

    inFlightFetch = fetchMe(meUrl)
      .then(function (result) {
        // 2026-09-16: a manually locked-out account (see
        // rewardsLockedResponse() in workers/loyalty/src/index.js) is a
        // designed response shape, not a transient failure -- surfaced
        // distinctly (`{ locked: true, message }`, never cached, never
        // published as a balance change) so every consumer can react to
        // it without each having to make its own separate request just to
        // check for this one case.
        if (!result.ok && result.json && result.json.code === 'rewards_locked') {
          return { locked: true, message: result.json.message };
        }
        if (!result.ok) throw new Error('rewards_state_refresh_failed');
        return set(result.json);
      })
      .catch(function (error) {
        console.error(error);
        return get(); // last-known state is still better than nothing
      })
      .then(function (state) {
        inFlightFetch = null;
        return state;
      });

    return inFlightFetch;
  }

  /**
   * Starts the one shared polling interval, if it isn't already running.
   * Safe to call from every consumer's own init -- only the first call
   * actually starts a timer; the rest just register their meUrl as a
   * fallback in case an earlier caller's element gets torn down first.
   */
  function startAutoRefresh(meUrl, pollMs) {
    knownMeUrl = knownMeUrl || meUrl;
    if (pollTimer) return;
    pollTimer = setInterval(function () {
      refresh(knownMeUrl);
    }, pollMs || POLL_MS);
  }

  /**
   * Subscribes to balance changes from ANY source -- a fetch this module
   * ran itself, or a direct set() call from a consumer that just redeemed/
   * cancelled/earned points and already knows the new number. Returns an
   * unsubscribe function, same convention as FoxTheme.pubsub.subscribe.
   */
  function subscribe(callback) {
    if (!(window.FoxTheme && FoxTheme.pubsub)) return function () {};
    return FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.rewardsBalanceChange, function (event) {
      callback(event.data);
    });
  }

  window.EnrichmentRewardsState = {
    get: get,
    set: set,
    isFresh: isFresh,
    refresh: refresh,
    fetchMe: fetchMe,
    startAutoRefresh: startAutoRefresh,
    subscribe: subscribe,
  };
})();
